import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import axios from "../../api/axios";
import { getAttributeValue } from "../../utils/utils";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";
import { formatDimensionLabel, isBlankDimension } from "./AprViewToggle";

const PROJECT_PROGRAM = "g3wMUKEMmH3";
const PROJECT_TYPE_ATTRIBUTE = "wtf3BR2YO8w";
const PROJECT_TYPE_VALUE = "Project";
const IMPLEMENTATION_STAGE = "rRWa5ghYO35";
const EXPENDITURE_ELEMENT = "jr8gk707kAw";
const IMPLEMENTATION_STATUS_ELEMENT = "f1T48vHfJc1";

function getStageEventValue(reports, trackedEntity, stageId, dataElementId) {
  const stageEvent = reports.find(
    (report) =>
      report.trackedEntity === trackedEntity
      && report.programStage === stageId
      && Array.isArray(report.dataValues)
      && report.dataValues.length > 0,
  );

  if (!stageEvent) {
    return "";
  }

  const match = stageEvent.dataValues.find(
    (dataValue) => dataValue.dataElement === dataElementId,
  );

  return match?.value ?? "";
}

const Table_3 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getProjects();
  }, [selectedYear, district, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_3",
      tableData,
    }),
  }));

  async function getProjects() {
    if (!district) {
      setTableData([]);
      return;
    }

    try {
      const startDate = `${selectedYear}-01-01`;
      const endDate = `${selectedYear}-12-31`;

      const [entitiesResp, eventsResp] = await Promise.all([
        axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=${PROJECT_PROGRAM}&filter=${PROJECT_TYPE_ATTRIBUTE}:EQ:${PROJECT_TYPE_VALUE}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
        axios.get(
          `/tracker/events?program=${PROJECT_PROGRAM}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
      ]);

      const projects = entitiesResp.data.instances || [];
      const reports = eventsResp.data.instances || [];

      if (projects.length === 0) {
        setTableData([]);
        return;
      }

      const temps = projects.map((project, idx) => {
        const contractSum = getAttributeValue("Contract Sum", project);
        const expenditureRaw = getStageEventValue(
          reports,
          project.trackedEntity,
          IMPLEMENTATION_STAGE,
          EXPENDITURE_ELEMENT,
        );
        const implementationStatusRaw = getStageEventValue(
          reports,
          project.trackedEntity,
          IMPLEMENTATION_STAGE,
          IMPLEMENTATION_STATUS_ELEMENT,
        );

        const expenditure = parseFloat(expenditureRaw) || 0;
        const contractSumValue = parseFloat(contractSum) || 0;
        const rawDimension = getAttributeValue("Development Dimension", project);

        return {
          no: idx + 1,
          code: getAttributeValue("ProjectID", project),
          description: getAttributeValue("Description", project),
          dimension: isBlankDimension(rawDimension) ? "" : formatDimensionLabel(rawDimension),
          location: getAttributeValue("Location", project),
          contractor: getAttributeValue("Contractor", project),
          contractSum: contractSum === "N/A" ? "" : contractSum,
          fundingSource: getAttributeValue("Primary Funding Source", project),
          dateOfAward: getAttributeValue("Awarded Date", project),
          dateStarted: getAttributeValue("Start Date", project),
          expectedCompletion: getAttributeValue("Expected Completion Date", project),
          expenditure: expenditureRaw || 0,
          outstanding: contractSumValue - expenditure,
          implementationStatus: implementationStatusRaw || "",
          remarks: getAttributeValue("Remarks", project),
        };
      });

      setTableData(temps);
    } catch (err) {
      console.error("Error in getProjects:", err);
      setTableData([]);
    }
  }

  const tableStyle = {
    border: "1px solid #000",
    borderCollapse: "collapse",
    width: "100%",
    marginTop: "12px",
    minWidth: "1100px",
  };

  const thStyle = {
    border: "1px solid #000",
    fontWeight: "bold",
    backgroundColor: "#d4edda",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    border: "1px solid #000",
  };

  return (
    <div className="col-12">
      <h3>Table 3 – Project Register</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          <p style={{ textAlign: "justify", marginBottom: "1rem" }}>
            The project register lists physical projects implemented during the year, including the
            development dimension, location, contractor, contract sum, funding source, schedule,
            expenditure to date, implementation status, and remarks. Scroll horizontally to review
            the full register as one coherent table.
          </p>
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table3-${year}`}
            hideTableDis={hideTableDis}
          />

          <div className="table-responsive">
            <table className="table table-bordered" style={tableStyle}>
              <thead>
                <tr style={{ border: "1px solid #000" }}>
                  <th style={thStyle}>Code</th>
                  <th style={thStyle}>Project Description</th>
                  <th style={thStyle}>Development Dimension</th>
                  <th style={thStyle}>Location</th>
                  <th style={thStyle}>Contractor</th>
                  <th style={thStyle}>Contract Sum GHȻ</th>
                  <th style={thStyle}>Source of Funding</th>
                  <th style={thStyle}>Date of Award</th>
                  <th style={thStyle}>Date Started</th>
                  <th style={thStyle}>Expected Completion</th>
                  <th style={thStyle}>Expenditure to Date</th>
                  <th style={thStyle}>Outstanding Balance</th>
                  <th style={thStyle}>Implementation Status %</th>
                  <th style={thStyle}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, index) => (
                    <tr key={index}>
                      <td style={tdStyle}>{row.code}</td>
                      <td style={tdStyle}>{row.description}</td>
                      <td style={tdStyle}>{row.dimension}</td>
                      <td style={tdStyle}>{row.location}</td>
                      <td style={tdStyle}>{row.contractor}</td>
                      <td style={tdStyle}>{row.contractSum}</td>
                      <td style={tdStyle}>{row.fundingSource === "N/A" ? "" : row.fundingSource}</td>
                      <td style={tdStyle}>{row.dateOfAward === "N/A" ? "" : row.dateOfAward}</td>
                      <td style={tdStyle}>{row.dateStarted === "N/A" ? "" : row.dateStarted}</td>
                      <td style={tdStyle}>{row.expectedCompletion === "N/A" ? "" : row.expectedCompletion}</td>
                      <td style={tdStyle}>{row.expenditure}</td>
                      <td style={tdStyle}>{row.outstanding}</td>
                      <td style={tdStyle}>{row.implementationStatus}</td>
                      <td style={tdStyle}>{row.remarks === "N/A" ? "" : row.remarks}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="14" style={{ textAlign: "center" }}>
                      No data available for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table3-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_3;
